import { Inject, Injectable, makeStateKey, Optional, PLATFORM_ID, TransferState } from '@angular/core';
import { Resolve, ActivatedRouteSnapshot, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { DataService } from './data/data.service';
import { GetStorePageQuery, GetStorePageQueryVariables } from '../../common/generated-types';
import { GET_STORE_PAGE } from '../../common/graphql/documents.graphql';
import { NGXLogger } from 'ngx-logger';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { StateService } from './state/state.service';
import { log } from 'console';

const PAGE_RESOLVER_STORE_IDENTIFIER_KEY = makeStateKey<string>('PAGE_RESOLVER_STORE_IDENTIFIER');

@Injectable({ providedIn: 'root' })
export class PageResolver implements Resolve<any> {
  slug: string;
  storeIdentifier: string;
  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    @Optional() @Inject('STORE_IDENTIFIER') public storeIdentifierFromSSR: string | null,
    private dataService: DataService,
    private stateService: StateService,
    private transferState: TransferState,
    private logger: NGXLogger,
    private router: Router
  ) {}

  resolve(route: ActivatedRouteSnapshot): Observable<any> {
    const routePath = route.pathFromRoot
    .map(r => r.url.map(segment => segment.path).join('/'))
    .filter(path => path.length > 0)
    .join('/');
    this.slug = '/' + routePath.split('?')[0].toLowerCase();
    if (isPlatformServer(this.platformId)) {
      this.storeIdentifier = this.storeIdentifierFromSSR || '';
      this.transferState.set(PAGE_RESOLVER_STORE_IDENTIFIER_KEY, this.storeIdentifier);
    }
    return this.stateService.select(state => state.storeIdentifier).pipe(
      switchMap(storeIdentifier => {
        if (isPlatformBrowser(this.platformId)) {
          if(!storeIdentifier) {
            this.storeIdentifier = this.transferState.get(PAGE_RESOLVER_STORE_IDENTIFIER_KEY, '');
            this.transferState.remove(PAGE_RESOLVER_STORE_IDENTIFIER_KEY);
          } else {
            this.storeIdentifier = storeIdentifier;
          }
        }
        this.logger.debug(`PageResolver for ${this.slug} ${storeIdentifier}`);
        return this.dataService.query<GetStorePageQuery, GetStorePageQueryVariables>(GET_STORE_PAGE, {
          identifier: this.storeIdentifier,
          slug: this.slug
        }).pipe(
          map(result => {
            this.logger.debug(`PageResolver: result ${result}`);
            if (result?.storePage) {
              return result?.storePage;
            } else {
              this.logger.error(`PageResolver: Page not found for slug '${this.slug}'`);
              this.router.navigateByUrl('/not-found', { skipLocationChange: true });
              return null;
            }
          }),
          catchError((err) => {
            // In case of errors, also go to not-found
            this.logger.error(`PageResolver: Error ${err} fetching page for slug '${this.slug}'`);
            this.router.navigateByUrl('/not-found', { skipLocationChange: true });
            return of(null);
          })
        )
      })
    );
  }
}
